import axiosClient from "./axiosClient";
import validator from "validator/es";
import {getIdToken} from "./firebase";
import {
  ALLOWED_SAML_TYPES,
  API,
  API_RES_CODE,
  AUTH_ABSENT_TOKEN_STUB,
  DISPATCHER_ENDPOINT,
  LOG_IT_ENDPOINT,
  LOG_SEVERITIES,
  OTP_EVENTS,
  REDIRECT_TYPES,
  SAML_PROVIDER_MAP,
  CLINICIAN_TERMS_VERSION,
  V2_API_ACCEPT,
  V2_PATHS,
} from "../assets/appConstants";
import SystemPromptService from "./systemPromptService";
import {ALERTS} from "../components/common/systemAlert";
import axios from "axios";
import {redirect} from "./utils";

const GEO_LOCATION_URL = "https://ipapi.co/json";
const GEO_CACHE_TTL_MS = 10 * 60 * 1000;
// Failures (rate limiting, timeouts) expire quickly so a later step can still resolve a country.
const GEO_FAILURE_CACHE_TTL_MS = 30 * 1000;

const ApiService = {
  frontSession: '',
  loginAppHost: 'default',
  appVersion: '401.4-1.0.0-0', // See the documentation in the backend \UtilityManager::parseDeviceVersion().
  getFrontSessionId() {
    //set unique id for tracing
    const LsKey = 'frontSession'
    //save to local storage if not exist
    if (!localStorage.getItem(LsKey)) {
      const rendStr = (Math.random() + 1).toString(36).substring(2)
      localStorage.setItem(LsKey, 'login-' + rendStr)
    }
    //save to memory
    this.frontSession = this.frontSession || localStorage.getItem(LsKey)
    return this.frontSession
  },
  getAppHost() {
    return this.loginAppHost
  },
  setAppHost(host) {
    this.loginAppHost = host || this.loginAppHost
  },
  normalizeServiceUrl(url) {
    return (url || "").replace(/\/$/, "");
  },
  getBackendUrl(path) {
    if (path.startsWith("/v2/")) {
      return global.globalConfigTools.serviceDiscovery("api");
    }
    return global.globalConfigTools.serviceDiscovery("backend");
  },
  buildServiceUrl(path, query = {}) {
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    const base = this.normalizeServiceUrl(this.getBackendUrl(normalizedPath));
    const url = new URL(`${base}${normalizedPath}`);
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        url.searchParams.set(key, String(value));
      }
    });
    return url.toString();
  },
  async v2Headers(skipAuth = false) {
    const idToken = skipAuth ? AUTH_ABSENT_TOKEN_STUB : await getIdToken();
    return {
      Authorization: `Bearer ${idToken}`,
      Accept: V2_API_ACCEPT,
      frontSession: this.getFrontSessionId(),
      appVersion: this.appVersion,
    };
  },
  v2Url(path, query = {}) {
    return this.buildServiceUrl(path, query);
  },
  async v2Get(path, options = {}) {
    const { quiet = false, query = {}, skipAuth = false } = options;
    if (!quiet) {
      SystemPromptService.showSpinner();
    }
    try {
      const response = await axios.get(this.v2Url(path, query), {
        headers: await this.v2Headers(skipAuth),
      });
      return this.processV2Response(response, { quiet });
    } catch (error) {
      if (!quiet) {
        this.handleError(error);
      } else {
        this.logDispatcherError(`quiet v2 GET ${path}`, error);
      }
      throw error;
    }
  },
  async v2Patch(path, data, options = {}) {
    const { quiet = false } = options;
    if (!quiet) {
      SystemPromptService.showSpinner();
    }
    try {
      const response = await axios.patch(this.v2Url(path), data, {
        headers: {
          ...(await this.v2Headers()),
          "Content-Type": "application/json",
        },
      });
      return this.processV2Response(response, { quiet });
    } catch (error) {
      if (!quiet) {
        this.handleError(error);
      } else {
        this.logDispatcherError(`quiet v2 PATCH ${path}`, error);
      }
      throw error;
    }
  },
  async v2Put(path, data, options = {}) {
    const { quiet = false } = options;
    if (!quiet) {
      SystemPromptService.showSpinner();
    }
    try {
      const response = await axios.put(this.v2Url(path), data, {
        headers: {
          ...(await this.v2Headers()),
          "Content-Type": "application/json",
        },
      });
      return this.processV2Response(response, { quiet });
    } catch (error) {
      if (!quiet) {
        this.handleError(error);
      } else {
        this.logDispatcherError(`quiet v2 PUT ${path}`, error);
      }
      throw error;
    }
  },
  async v2Post(path, data, options = {}) {
    const { quiet = false, skipAuth = false } = options;
    if (!quiet) {
      SystemPromptService.showSpinner();
    }
    try {
      const response = await axios.post(this.v2Url(path), data ?? {}, {
        headers: {
          ...(await this.v2Headers(skipAuth)),
          "Content-Type": "application/json",
        },
      });
      return this.processV2Response(response, { quiet });
    } catch (error) {
      if (!quiet) {
        this.handleError(error);
      } else {
        this.logDispatcherError(`quiet v2 POST ${path}`, error);
      }
      throw error;
    }
  },
  async v2PostMultipart(path, formData, options = {}) {
    const { quiet = true } = options;
    if (!quiet) {
      SystemPromptService.showSpinner();
    }
    try {
      const response = await axios.post(this.v2Url(path), formData, {
        headers: {
          ...(await this.v2Headers()),
          "Content-Type": "multipart/form-data",
        },
      });
      return this.processV2Response(response, { quiet });
    } catch (error) {
      if (!quiet) {
        this.handleError(error);
      } else {
        this.logDispatcherError(`quiet v2 POST multipart ${path}`, error);
      }
      throw error;
    }
  },
  processV2Response(response, options = {}) {
    const { quiet = false } = options;
    const body = response?.data;
    const httpStatus = response?.status;
    const isHttpSuccess = httpStatus >= 200 && httpStatus < 300;
    const bodyStatus = body?.statusCode;
    const isBodyStatusSuccess =
      typeof bodyStatus === "number" && bodyStatus >= 200 && bodyStatus < 300;
    const ok =
      isHttpSuccess ||
      isBodyStatusSuccess ||
      body?.code === API_RES_CODE.SUCCESS;
    if (ok) {
      if (!quiet) {
        SystemPromptService.hideSpinner();
      }
      if (body?.data !== undefined) {
        const data = body.data;
        if (data === "" || data === null) {
          return true;
        }
        return data;
      }
      if (body != null && typeof body === "object" && Object.keys(body).length > 0) {
        return body;
      }
      return true;
    }
    if (!quiet) {
      this.handleError(response);
    }
    return false;
  },
  updateAccountData(accountId, accountData) {
    const keys = Object.keys(accountData || {});
    if (keys.length === 1 && keys[0] === "password") {
      return this.updateAccountPassword(accountData.password, { quiet: false });
    }

    const params = {
      api: API.UPDATE_ACCOUNT_DETAILS,
      accountId,
      accountData,
    };

    return this.axiosFlow(params)
  },
  getTherapistMe() {
    return this.v2Get(V2_PATHS.THERAPISTS_ME, { quiet: true });
  },
  patchTherapistSubAccount(_accountId, payload) {
    return this.v2Patch(V2_PATHS.THERAPISTS_SUB_ACCOUNT, payload, { quiet: true });
  },
  updateAccountPassword(password, options = {}) {
    const { quiet = true } = options;
    return this.v2Put(V2_PATHS.ACCOUNTS_UPDATE_PASSWORD, { password }, { quiet });
  },
  acceptTermsAndConditions() {
    return this.v2Post(
      V2_PATHS.THERAPISTS_ACCEPT_TERMS_AND_CONDITIONS,
      {
        termsAccepted: true,
        termsVersion: CLINICIAN_TERMS_VERSION,
      },
      { quiet: true }
    );
  },
  getTherapistProfessions() {
    return this.v2Get(V2_PATHS.THERAPIST_PROFESSIONS, { quiet: true });
  },
  saveTherapistProfessions(_accountId, payload) {
    return this.v2Put(V2_PATHS.THERAPIST_PROFESSIONS, payload, { quiet: true });
  },
  updateAccountEmailOrPhone(_accountId, payload) {
    return this.v2Patch(V2_PATHS.ACCOUNTS_EMAIL_OR_PHONE, payload, { quiet: true });
  },
  updatePersonalDetails(_accountId, payload) {
    return this.v2Patch(V2_PATHS.ACCOUNTS_UPDATE_PERSONAL_DETAILS, payload, { quiet: true });
  },
  uploadProfilePhoto(_accountId, file) {
    const formData = new FormData();
    formData.append("file", file);
    return this.v2PostMultipart(V2_PATHS.ACCOUNTS_PROFILE_PHOTO, formData, { quiet: true }).then(
      (result) => (result === false ? false : true)
    );
  },
  /**
   * POST /v2/users/reset-password-request
   * Sends a reset-code email, or a continue-registration email when the
   * patient has not finished onboarding.
   */
  sendResetLink(identifier) {
    return this.v2Post(
      V2_PATHS.USERS_RESET_PASSWORD_REQUEST,
      { email: identifier },
      { skipAuth: true }
    );
  },
  /**
   * POST /v2/patients/reset-password/{patientId}
   * Clinician-triggered reset for a known patient.
   */
  resetPatientPassword(patientId, payload = {}) {
    return this.v2Post(`${V2_PATHS.PATIENTS_RESET_PASSWORD}/${patientId}`, payload);
  },
  /**
   * POST /v2/accounts/custom-token-by-reset-code
   * Exchange a password-reset code for a Firebase custom token. The code goes in
   * the body, never the query string, so it stays out of logs and history.
   */
  getCustomTokenByResetCode(code) {
    return this.v2Post(
      V2_PATHS.ACCOUNTS_CUSTOM_TOKEN_BY_RESET_CODE,
      { code },
      { quiet: true, skipAuth: true }
    ).then((result) => this.normalizeCustomTokenPayload(result));
  },
  normalizeCustomTokenPayload(result) {
    if (!result || result === true) {
      return result;
    }
    if (typeof result === "string") {
      return { customToken: result };
    }
    if (typeof result !== "object") {
      return result;
    }
    const customToken =
      result.customToken ||
      result.token ||
      result.firebaseToken ||
      result.firebaseCustomToken;
    return customToken ? { ...result, customToken } : result;
  },
  getCustomTokenByOtl(code) {
    return this.v2Post(
      V2_PATHS.ACCOUNTS_CUSTOM_TOKEN_BY_OTL,
      { code },
      { quiet: true, skipAuth: true }
    );
  },
  getCustomTokenForSubAccount(subAccountId) {
    return this.v2Get(`${V2_PATHS.ACCOUNTS_CUSTOM_TOKEN}/${subAccountId}`, {
      quiet: true,
    });
  },
  getAccountMyType() {
    return this.v2Get(V2_PATHS.ACCOUNTS_MY_TYPE, { quiet: true });
  },
  /** Registration profile — GET /v2/therapists/account/for-registration (sub-account token). */
  getTherapistForRegistration() {
    return this.v2Get(V2_PATHS.THERAPISTS_FOR_REGISTRATION, { quiet: true });
  },
  logDispatcherError(label, error) {
    const responseData = error?.response?.data ?? error?.data;
    this.logIt(label, LOG_SEVERITIES.NOTICE, {
      message: error?.message,
      response: responseData,
    });
  },
  loginHelper(accountId, idToken) {
    const params = {
      api: "accounts/login-helper",
      accountId,
      idToken,
    };
    return this.axiosFlow(params)
  },
  phoneLogin(phone, password) {
    const params = {
      api: "accounts/phone-login",
      phone,
      password,
    };
    return this.axiosFlow(params)
  },
  logOtpEvent(eventType, details = {}) {
    let message;
    const errorMessage = JSON.stringify(details.error);
    let severity = LOG_SEVERITIES.DEBUG;
    switch (eventType) {
      case OTP_EVENTS.SMS_SEND_SUCCESS:
        message = `OTP / SMS sent SUCCESS to ${details.phoneNumber}`;
        break;
      case OTP_EVENTS.SMS_SEND_ERROR:
        severity = LOG_SEVERITIES.WARNING;
        message = `OTP / Error sending SMS to ${details.phoneNumber}: ${details.error}`;
        break;
      case OTP_EVENTS.SMS_VERIFY_SUCCESS:
        message = "OTP / SMS verification successful.";
        break;
      case OTP_EVENTS.SMS_VERIFY_ERROR:
        severity = LOG_SEVERITIES.WARNING;
        message = `OTP / Error verifying SMS: ${details.error}`;
        break;
      case OTP_EVENTS.SMS_RESEND_SUCCESS:
        message = "OTP / Resending SMS SUCCESS";
        break;
      case OTP_EVENTS.SMS_RESEND_ERROR:
        severity = LOG_SEVERITIES.WARNING;
        message = `OTP / Error resending SMS: ${details.error}`;
        break;
      case OTP_EVENTS.OTL_SEND_SUCCESS:
        message = `OTP / OTL sent SUCCESS to ${details.phoneOrEmail}`;
        break;
      case OTP_EVENTS.OTL_SEND_ERROR:
        severity = LOG_SEVERITIES.WARNING;
        message = `OTP / Error sending OTL to ${details.phoneOrEmail}: ${details.error}`;
        break;
      case OTP_EVENTS.OTL_RESEND_SUCCESS:
        message = `OTP / OTL resent SUCCESS to ${details.phoneOrEmail}`;
        break;
      case OTP_EVENTS.OTL_RESEND_ERROR:
        severity = LOG_SEVERITIES.WARNING;
        message = `OTP / Error resending OTL to ${details.phoneOrEmail}: ${details.error}`;
        break;
      default:
        message = `OTP / Unknown OTP event type: ${eventType}`;
    }
    this.logIt(message, severity, details);
  },
  logIt(message, severity = LOG_SEVERITIES.DEBUG, objectData = {}) {
    message = 'LoginApp / ' + message;
    if (window.location.origin.indexOf('localhost') > -1) {
      console.log(message, JSON.stringify(objectData))
    }
    objectData.appHost = this.getAppHost()
    let frontSession = this.getFrontSessionId()
    let appVersion = this.appVersion

    let params = new URLSearchParams();
    params.append('message', message);
    params.append('severity', severity);
    params.append('objectData', JSON.stringify(objectData));
    params.append('metadata', JSON.stringify({frontSession, appVersion}));
    axios.post(LOG_IT_ENDPOINT, params).catch();
  },
  geoCache: null,
  geoRequest: null,
  getGeoLocation({ retryFailed = false } = {}) {
    if (this.geoCache?.expiry > Date.now() && !(retryFailed && !this.geoCache.data)) {
      return Promise.resolve(this.geoCache.data);
    }
    if (this.geoRequest) {
      return this.geoRequest;
    }

    this.geoRequest = axios
      .get(GEO_LOCATION_URL, { timeout: 4000 })
      .then(({ data }) => (data?.error ? null : data))
      .catch(() => null)
      .then((data) => {
        const ttl = data ? GEO_CACHE_TTL_MS : GEO_FAILURE_CACHE_TTL_MS;
        this.geoCache = { data, expiry: Date.now() + ttl };
        return data;
      })
      .finally(() => {
        this.geoRequest = null;
      });

    return this.geoRequest;
  },
  async axiosFlow(params, options = {}) {
    const { quiet = false } = options;
    if (!quiet) {
      SystemPromptService.showSpinner();
    }
    return await new Promise((resolve, reject) => {
      getIdToken()
        .then((IdToken) => {
          const reqConfig = {
            headers: {
              Authorization: `Bearer ${IdToken}`,
              frontSession: this.getFrontSessionId(),
              appVersion: this.appVersion
            },
          }
          axiosClient
            .sendToDispatcher(params, reqConfig)
            .then((res) => {
              resolve(this.processResponse(res, { quiet }))
            })
            .catch((err) => {
              if (!quiet) {
                this.handleError(err);
              } else {
                this.logDispatcherError(`quiet dispatcher error: ${params.api}`, err);
              }
              reject(err);
            })
        })
    })
  },
  async axiosFlowMultipart(formData) {
    return await new Promise((resolve, reject) => {
      getIdToken()
        .then((IdToken) => {
          axiosClient
            .sendPostRequest(DISPATCHER_ENDPOINT(), formData, {
              headers: {
                Authorization: `Bearer ${IdToken}`,
                frontSession: this.getFrontSessionId(),
                appVersion: this.appVersion,
              },
            })
            .then((res) => resolve(this.processResponse(res, { quiet: true })))
            .catch((err) => reject(err));
        });
    });
  },

  sendOneTimeLink(email) {
    return this.v2Post(
      V2_PATHS.USERS_SEND_ONE_TIME_LINK,
      { email },
      { skipAuth: true }
    );
  },

  reCaptchaSmsDefenceCreateAssessment(reCaptchaSmsDefenceConfig, token, action) {
    const url = reCaptchaSmsDefenceConfig.backend;
    const headers = {
      'Content-Type': 'application/json',
    };
    const params = {
      projectId: global.globalConfigTools.serviceDiscovery('firebase').projectId,
      siteKey: reCaptchaSmsDefenceConfig.siteKey,
      token,
      action
    };
    return axios.get(url, {headers, params});
  },

  processResponse(response, options = {}) {
    const { quiet = false } = options;
    if (response.data.code === API_RES_CODE.SUCCESS) {
      if (!quiet) {
        SystemPromptService.hideSpinner();
      }
      const data = response.data.data;
      if (data === "" || data == null) {
        return true;
      }
      return data;
    }
    if (!quiet) {
      this.handleError(response);
    }
    return false;
  },
  handleError(error) {
    ApiService.logIt("error caught:" + ApiService.findErrorDescription(error), LOG_SEVERITIES.WARNING, error)

    let alertObj = ALERTS.GENERIC

    let extractError
    const errCode = error?.data?.code ?? error?.response?.data?.code ?? ''
    if (errCode) {
      switch (errCode) {
        case API_RES_CODE.UNAUTHORIZED:
          alertObj.content = "unauthorized.message"
          break
        case API_RES_CODE.ERROR_EXPIRE_CODE:
          alertObj.title = "expire.code.title"
          alertObj.content = "expire.code.message"
          alertObj.hideCloseBtn = true
          alertObj.btnAction = {
            show: true,
            action: () => {
              redirect(REDIRECT_TYPES.FORGOT_PASSWORD)
            },
            title: "close"
          }
          break
        case API_RES_CODE.PASSWORD_TOO_WEAK:
          alertObj.title = "password.too.weak.title"
          alertObj.content = "password.too.weak.message"
          break
        case API_RES_CODE.ERROR_LOGIN_ACCOUNT_FAILURE:
          alertObj.title = "email.wasnt.sent.title"
          alertObj.content = "email.wasnt.sent.message"
          break
        case API_RES_CODE.LOGIN_FAIL_BAD_CREDENTIALS:
          alertObj.title = "login.failed.title"
          alertObj.content = "login.failed.message"
          break
        case API_RES_CODE.BAD_RESET_CODE:
          alertObj = ALERTS.INVALID_OTL_CODE
          break
        case API_RES_CODE.ERROR_USER_ALREADY_ONBOARDED:
          alertObj.title = "code.used.title"
          alertObj.content = "code.used.message"
          alertObj.hideCloseBtn = true
          alertObj.btnAction = {
            show: true,
            action: () => {
              redirect(REDIRECT_TYPES.HOME)
            },
            title: "close"
          }

          break
        default:
          alertObj.description = "server.error"
          extractError = error
      }
    }
    //pass the error only for unknown err codes (prevent "findErrorDescription" to prompt the error from the thrown error
    SystemPromptService.showAlert(alertObj, extractError)
    return {alertObj, extractError}
  },
  findErrorDescription(errorCaught) {
    if (!errorCaught) return ''
    if (typeof errorCaught === 'string') return errorCaught
    return errorCaught?.description ?? errorCaught?.data?.description ?? errorCaught?.response?.data?.description ?? ''
  }

}

export default ApiService;

export const validateEmail = (email) => {
  if (typeof email !== "string") return false;
  return validator.isEmail(email.trim());
};

export const validatePhone = (phone) => {
  if (typeof phone !== "string") return false;
  return validator.isMobilePhone(phone.trim(), 'any');
};

export const convertPhoneToMail = (phone) => {
  return phone + "@wizecare.com";
};

export const checkSamlType = (email) => {
  const domain = email.split('@')[1];
  const samlType = SAML_PROVIDER_MAP[domain];
  return ALLOWED_SAML_TYPES.includes(samlType) ? samlType : null;
};

